// SPDX-License-Identifier: GPL-3.0-only
// Original THINGS weapon models, attached as DrawWeapons in OpenSaga render.cpp.
import * as THREE from 'three';
import { DDSLoader } from 'three/addons/loaders/DDSLoader.js';
import { decodeMesh, decodeFaceon, parseGSC } from './gsc.mjs';
import { evaluatePointOfInterest } from './ghg.mjs';

// CC_streak assigns red0,green1,blue2,purple3; DrawWeapons maps these to
// ObjTab101..108. Blue's authored glow is "lightsabreglow" (no blue suffix).
const saberModels = {
  red: { names: ['lightsabrehandle', 'lightsabrered', 'lightsabreglowred'], objectIds: [17, 101, 102] },
  green: { names: ['lightsabrehandle', 'lightsabregreen', 'lightsabreglowgreen'], objectIds: [17, 103, 104] },
  blue: { names: ['lightsabrehandle', 'lightsabreblue', 'lightsabreglow'], objectIds: [17, 105, 106] },
  purple: { names: ['lightsabrehandle', 'lightsabrepurple', 'lightsabreglowpurple'], objectIds: [17, 107, 108] },
};
export function resolveOriginalSaberModels(config, { color } = {}) {
  const values = config.values ?? config;
  if (['darthmaul', 'countdooku', 'grievous'].includes(String(values.variant).toLowerCase())) throw new Error('Original character uses a specialized saber/hilt mapping.');
  let selected = color;
  if (selected === undefined && values.weapon !== undefined) {
    selected = Object.entries(saberModels).find(([, mapping]) => mapping.names[1] === String(values.weapon).toLowerCase())?.[0];
    if (!selected) throw new Error('Unsupported original explicit saber weapon model.');
  }
  selected ??= values.streak;
  if (typeof selected !== 'string' || !Object.hasOwn(saberModels, selected.toLowerCase())) throw new Error('Missing original saber color configuration.');
  selected = selected.toLowerCase();
  return { color: selected, names: saberModels[selected].names.slice(), objectIds: saberModels[selected].objectIds.slice() };
}

export function createSaber(buffer, character, options = {}) {
  const selected = resolveOriginalSaberModels(character.config, options);
  const parsed = buffer?.specialObjects ? buffer : parseGSC(buffer), root = new THREE.Group(); root.matrixAutoUpdate = false;
  const textures = new Map(), materials = [], geometries = [], dds = new DDSLoader();
  for (const name of selected.names) {
    const special = parsed.specialObjects.find(special => special.name.toLowerCase() === name);
    if (!special?.parts.length) throw new Error(`Missing original weapon model: ${name}.`);
    for (const part of special.parts) {
      const faceon = part.type === 'faceon';
      const decoded = faceon ? decodeFaceon(parsed, part) : decodeMesh(parsed, part.mesh, part.material);
      if (faceon && decoded.orientation !== 0) throw new Error('Unsupported weapon billboard orientation.');
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(decoded.positions, 3));
      geometry.setAttribute('uv', new THREE.BufferAttribute(decoded.uvs, 2));
      geometry.setAttribute('color', new THREE.BufferAttribute(decoded.colors, faceon ? 4 : 3));
      geometry.setIndex(new THREE.BufferAttribute(decoded.indices, 1));
      if (faceon) geometry.setAttribute('faceonOffset', new THREE.BufferAttribute(decoded.offsets, 2));
      else if (decoded.hasNormals) geometry.setAttribute('normal', new THREE.BufferAttribute(decoded.normals, 3));
      else geometry.computeVertexNormals();
      const source = part.material;
      let map = textures.get(source.texture) ?? null;
      if (!map && parsed.textures.has(source.texture)) {
        const image = dds.parse(parsed.textures.get(source.texture).data, true);
        if (!image.format || image.isCubemap) throw new Error('Unsupported weapon texture.');
        map = image.format === THREE.RGBAFormat
          ? new THREE.DataTexture(image.mipmaps[0].data, image.width, image.height, THREE.RGBAFormat)
          : new THREE.CompressedTexture(image.mipmaps, image.width, image.height, image.format);
        map.flipY = false; map.colorSpace = THREE.SRGBColorSpace; map.needsUpdate = true;
        textures.set(source.texture, map);
      }
      const material = faceon || name !== 'lightsabrehandle'
        ? new THREE.MeshBasicMaterial({ map, color: 0xffffff, vertexColors: true, transparent: true,
          blending: faceon ? THREE.AdditiveBlending : THREE.NormalBlending, depthWrite: !faceon, side: THREE.DoubleSide })
        : new THREE.MeshLambertMaterial({ map, color: map ? 0xffffff : new THREE.Color(...source.color.slice(0, 3)), vertexColors: decoded.hasColors });
      if (faceon) {
        material.onBeforeCompile = shader => {
          shader.vertexShader = 'attribute vec2 faceonOffset;\n' + shader.vertexShader;
          shader.vertexShader = shader.vertexShader.replace('#include <project_vertex>',
            'vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);\n' +
            'mvPosition.xy += faceonOffset;\n' +
            'gl_Position = projectionMatrix * mvPosition;');
        };
        material.customProgramCacheKey = () => 'original-faceon-orientation-0';
      }
      const mesh = new THREE.Mesh(geometry, material); mesh.frustumCulled = false; mesh.name = special.name;
      root.add(mesh); geometries.push(geometry); materials.push(material);
    }
  }
  character.model.add(root); root.visible = false;
  return {
    root, summary: { originalModels: 3, meshes: root.children.length, color: selected.color, modelNames: selected.names, objectIds: selected.objectIds },
    update(visible) {
      const poi = evaluatePointOfInterest(character.parsed, character.config.values.weapon_locator, character.matrices);
      root.visible = visible && !!poi; if (poi) root.matrix.fromArray(poi);
    },
    dispose() { root.removeFromParent(); geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose()); textures.forEach(t => t.dispose()); },
  };
}

// Existing callers retain the explicit green-only factory contract.
export function createGreenSaber(buffer, character) { return createSaber(buffer, character, { color: 'green' }); }
